import React, { useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { works } from "../mock/mock";
import { Reveal } from "../components/Reveal";

const Work = () => {
  const [hovered, setHovered] = useState(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 160, damping: 22, mass: 0.6 });
  const sy = useSpring(y, { stiffness: 160, damping: 22, mass: 0.6 });

  const onMouseMove = (e) => {
    x.set(e.clientX + 28);
    y.set(e.clientY - 110);
  };

  return (
    <section
      id="work"
      data-theme="light"
      className="sec-light relative px-6 md:px-12 py-28 md:py-40"
      onMouseMove={onMouseMove}
      data-testid="work-section"
    >
      <div className="max-w-[1240px] mx-auto">
        <Reveal>
          <div className="eyebrow">03 · My Work</div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display font-extrabold text-[32px] md:text-[52px] leading-[1.02] t-fg tracking-[-0.035em] mt-8 mb-16 md:mb-24 max-w-[22ch]">
            Building the architecture, not the project<span className="t-terra">.</span>
          </h2>
        </Reveal>

        <div>
          {works.map((w, i) => (
            <Reveal key={w.n} delay={Math.min(i * 0.05, 0.15)}>
              <article
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                className="group py-14 md:py-20 border-t"
                style={{ borderColor: "var(--hair-soft)" }}
                data-testid={`work-item-${w.n}`}
              >
                <div className="grid grid-cols-12 gap-x-6 md:gap-x-16 gap-y-8">
                  <div className="col-span-12 md:col-span-5">
                    <div className="text-[11px] font-semibold tracking-[0.24em] uppercase t-dim mb-5">
                      {w.geography} <span className="t-terra mx-1">·</span> {w.years}
                    </div>
                    <h3 className="font-display font-bold text-[24px] md:text-[34px] leading-[1.1] t-fg tracking-[-0.03em] max-w-[20ch] transition-colors duration-500 group-hover:text-terra">
                      {w.title}
                    </h3>
                    {w.link && (
                      <a
                        href={w.link}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-7 inline-flex items-center gap-1.5 text-[13px] font-medium t-fg"
                        data-testid={`work-link-${w.n}`}
                      >
                        <span className="link-underline">{w.linkLabel}</span>
                        <ArrowUpRight size={14} strokeWidth={1.6} />
                      </a>
                    )}
                  </div>
                  <div className="col-span-12 md:col-span-7">
                    <div className="space-y-5 text-[15px] md:text-[17px] leading-[1.8] t-fg opacity-75 group-hover:opacity-95 transition-opacity duration-700">
                      {w.body.map((p, idx) => (
                        <p key={idx}>{p}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Floating cover image — desktop hover only */}
      <AnimatePresence>
        {hovered !== null && works[hovered].cover && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            style={{ x: sx, y: sy }}
            className="pointer-events-none fixed top-0 left-0 z-40 hidden lg:block"
            aria-hidden="true"
          >
            <img
              src={works[hovered].cover}
              alt=""
              className="w-[280px] h-[190px] object-cover portrait-bw rounded-sm shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Work;
